import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { LangCode } from "../data/types.js";
import { t } from "../i18n/strings.js";

const STORAGE_KEY = "kra-map-welcomed";

interface Props {
  lang?: LangCode;
}

export function WelcomeModal({ lang = "en" }: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {}
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className="absolute inset-0"
            style={{ background: "rgba(0,0,0,0.4)" }}
            onClick={dismiss}
          />
          <motion.div
            className="relative mx-4 mb-8 w-full max-w-sm rounded-3xl bg-white p-6 text-center"
            style={{
              marginBottom: "calc(env(safe-area-inset-bottom, 8px) + 32px)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
            }}
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              delay: 0.1,
            }}
          >
            <p className="text-2xl mb-1">👘</p>
            <h2 className="text-lg font-semibold text-ink-0 font-display">
              {t("welcome.title", lang)}
            </h2>
            <p className="text-xs text-ink-2 mt-0.5 mb-3">
              {t("welcome.by", lang)}
            </p>
            <p className="text-sm text-ink-1 leading-relaxed">
              {t("welcome.desc", lang)}
            </p>
            <button
              onClick={dismiss}
              className="mt-5 w-full py-3 rounded-2xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
              style={{ backgroundColor: "#B76E6E" }}
            >
              {t("welcome.explore", lang)}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
